import React, { InputHTMLAttributes } from 'react';
import { useTheme } from 'styled-components';
import { FormState } from 'src/types';
import {
  StyledButtonIcon,
  StyledTextInput,
  TextInputWrapper,
} from './TextInput.styles';

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  state: FormState;
  'data-testid'?: string;
  forwardRef?: React.Ref<HTMLInputElement>;
  $removeMarginBottom?: boolean;
}

export const TextInput: React.FC<TextInputProps> = ({
  state,
  type,
  forwardRef,
  $removeMarginBottom,
  ...props
}: TextInputProps) => {
  const theme = useTheme();
  const [showPassword, setShowPassword] = React.useState(false);
  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const isPasswordField = type === 'password';
  const isSearchField = type === 'search';

  const fieldType = () => {
    if (!isPasswordField) {
      return type ?? 'text';
    }
    if (showPassword) {
      return 'text';
    }
    return 'password';
  };

  const isReadOnly = state === FormState.READONLY;

  return (
    <TextInputWrapper>
      <StyledTextInput
        {...props}
        readOnly={isReadOnly}
        aria-readonly={isReadOnly}
        state={state}
        disabled={state === FormState.DISABLED}
        data-testid={props['data-testid']}
        id={props['data-testid']}
        aria-label="Text Input"
        type={fieldType()}
        ref={forwardRef}
        $removeMarginBottom={$removeMarginBottom}
      />
      {isPasswordField && (
        <span>
          <StyledButtonIcon
            className={showPassword ? 'uil uil-eye-slash' : 'uil uil-eye'}
            size="24px"
            color={theme.atlas.semantic.color.type.default}
            onClick={handleClickShowPassword}
            role="button"
            aria-label="show hide button"
            tabIndex={0}
            aria-pressed="false"
            onKeyDown={(e) =>
              e.key === 'Enter' && setShowPassword(!showPassword)
            }
            isClickable
            $adjustIconTopMargin={$removeMarginBottom ? '8px' : '0'}
          />
        </span>
      )}
      {isSearchField && (
        <span>
          <StyledButtonIcon
            className="uil uil-search"
            size="20px"
            color={theme.atlas.semantic.color.type.default}
            tabIndex={0}
          />
        </span>
      )}
    </TextInputWrapper>
  );
};
